import { isValidElement } from "react";
import { MnObject } from "backbone.marionette";
import { ReactToMarionetteView } from "extensions/marionette";
import Radio from "backbone.radio";

import strings from "common/strings";
import { emitter } from "common/emitter";
import { store } from "common/store";
import { ActiveUser } from "common/user";

import { NotImplementedError } from "common/helpers/errors";
import { APP_READY } from "common/actions/config";

import { SET_ACTIVE_ROUTE, SET_ACTIVE_PAGE_NAME } from "common/actions/common";

import { pazienHistory } from "common/components/pazienHistory";
import { showError, ensureErrorCallback } from "common/components/error";
import { getters } from "common/components/getters/getters";
import { LoadingPageView } from "common/components/loadingPageView";
import { Sidebar } from "common/features/leftSidebar/sidebar";
import { TopBar } from "common/features/topbar/topBar";
import { TopBar as ReactTopBar } from "common/features/topbar/reactTopBar";

import { Page } from "common/components/page/page";

export const PazienController = MnObject.extend({
    channelName: "main",

    radioEvents: {
        navigate: "navigateTo"
    },

    radioRequests: {
        navigate: "navigateTo",
        "get:user": "getActiveUser", // Radio.channel("main").request("get:user")
        "get:merchant": "getActiveMerchant", // Radio.channel("main").request("get:merchant")
        "get:getters": "getDataGetters", // Radio.channel("main").request("get:getters")
        "get:filters": "getSavedFilters", // Radio.channel("main").request("get:filters")
        "disabled:permission": "handleDisabledPermission",
        "show:package:compare": "showPackageComparison",
        "get:app:view": "getApplicationRegionCurrentView",
        "show:app:view": "addViewToApplicationRegion",
        "show:loading:view": "showLoadingView",
        "show:sidepane": "showSidePane",
        "get:page": "getCurrentPageParams",
        "ensure:background:content": "ensureBackgroundContent"
    },

    constructor: function constructor(...args) {
        MnObject.call(this, ...args);

        this.resetActiveUser();
        emitter.on("reset:user", this.resetActiveUser.bind(this));
    },

    getCurrentPageParams() {
        return {
            product: pazienHistory.getCurrentFragmentProduct(),
            fragment: window.decodeURIComponent(pazienHistory.getCurrentFragment())
        };
    },

    instantiateActiveUser() {
        this.user = new ActiveUser();
        return this.user;
    },

    resetActiveUser() {
        if (this.user) {
            this.user.cleanUp();
            delete this.user;
            emitter.emit("reset:user:success");
        }
    },

    getActiveUser() {
        return this.user || this.instantiateActiveUser();
    },

    getActiveMerchant() {
        return this.getActiveUser()?.activeMerchant;
    },

    getSavedFilters() {
        return this.getActiveUser()?.filters;
    },

    getDataGetters() {
        return getters;
    },

    async handleDisabledPermission(name, packageLevel = true, content = () => "Your account does not have Pazien viewing privileges for this page.") {
        if (!packageLevel) {
            const dialogOptions = {
                title: strings.accessDenied,
                dismissable: true,
                content,
                render: true
            };

            try {
                const { Dialog } = await import(
                    /* webpackChunkName: "dialog" */
                    "common/components/dialog/dialog"
                );
                Dialog.show(dialogOptions);
            } catch (error) {
                ensureErrorCallback(error);
            }
        } else {
            this.showPackageComparison();
        }
    },

    async showPackageComparison() {
        try {
            const { showPackageCompare } = await import(
                /* webpackChunkName: "package_comparison" */
                "common/features/packageInfo/packageCompare"
            );
            showPackageCompare();
        } catch (error) {
            ensureErrorCallback(error);
        }
    },

    getDefaultRoute() {
        throw new NotImplementedError();
    },

    getLoginRoute() {
        throw new NotImplementedError();
    },
    
    ensureBackgroundContent() {
        const currentView = this.getApplicationRegionCurrentView();
        if (currentView === null) {
            // Store current fragment in the variable as main() method will change it
            const currentFragment = pazienHistory.getCurrentFragment();

            this.keepOverlays = true;

            // Ugly semaphore to make sure that `addViewToApplicationRegion` will not close overlay that called for ensureBackgroundContent
            // TODO: can we do better?
            window.setTimeout(() => {
                delete this.keepOverlays;
            }, 1000);

            // And now silently change back to the original route without replacing the current one
            pazienHistory.navigateWithParams(currentFragment);
        }
    },

    closeOverlays() {
        Radio.channel("account").trigger("close:overlays");
    },

    navigateTo(route) {
        pazienHistory.navigateWithParams(route, { trigger: true });
    },

    getLayout: () => Radio.channel("app").request("get:layout"),

    showLoadingView(props) {
        const propsClone = { ...props };

        // We should delete asyncHeaderBar from loading view props because it will throw errors down the rendering path

        delete propsClone.asyncHeaderBar;
        this.addViewToApplicationRegion(new LoadingPageView(propsClone));
    },

    async addViewToApplicationRegion(view, options = {}) {
        if (isValidElement(view)) {
            view = new ReactToMarionetteView({
                component: view,
                ...options
            });
        }
        const radioChannel = this.getChannel();

        if (!this.keepOverlays) {
            this.closeOverlays();
        }

        if (!this.getApplicationRegionCurrentView()) {
            // Insert page view into main content region
            const layoutView = this.getLayout();
            if (!layoutView) {
                showError(view);
                return;
            }

            layoutView.showChildView("content", new Page());

            // Insert left sidebar
            layoutView.showChildView("leftSidebar", <Sidebar />);

            // Insert topBar
            if (store.getState().common.user.superuser && store.getState().settings.reactTopbar) {
                this.getLayout().showChildView("topBar", <ReactTopBar />, { waitForAttach: true });
            } else {
                const topBar = new TopBar({
                    model: this.user
                });
                // Listen for click events from to the top menu
                this.listenTo(topBar, "click:notifications", this.showNotifications);
                this.getLayout().showChildView("topBar", topBar);
            }
        }

        // Update top region with page name, section options, actions, datepicker, etc.
        // We should re-render the view instead of changing it's properties and hiding/showing subs
        try {
            const { HeaderBar } = await import(
                /* webpackChunkName: "header_bar" */
                /* webpackPrefetch: true */
                "common/components/headerBar/headerBar"
            );

            this.getLayout().getChildView("content").showHeader(new HeaderBar({
                mainView: view
            }));
        } catch (error) {
            ensureErrorCallback(error);
        }

        // Change Page name
        const pageName = view.getOption("name") || view.getOption("component")?.props?.options?.name;

        if (pageName) {
            const name = pageName.toLowerCase().replace(/ /g, "_");
            SET_ACTIVE_PAGE_NAME(name);
            this.getLayout().updatePageName(name);
        }

        SET_ACTIVE_ROUTE(pazienHistory.getCurrentFragment());
        radioChannel.trigger("before:add:view", view);
        APP_READY();
        this.getLayout().getChildView("content").showMain(view);
        radioChannel.trigger("after:add:view", view);
    },

    /**
     * Return currently active view of the main app region.
     */
    getApplicationRegionCurrentView(closeOverlays) {
        try {
            const view = this.getLayout().getChildView("content").getMain();
            if (view && closeOverlays) {
                this.closeOverlays();
            }
            return view;
        } catch {
            return null;
        }
    },

    showSidePane(view, side = "left") {
        view.render();
        view.el.classList.add(`side-${side}`);
        view.el.classList.add("behind-page");
        const wrapper = document.getElementById("wrapper");
        if (wrapper) {
            wrapper.appendChild(view.el);
            setTimeout(() => {
                view.el.classList.remove("behind-page");
            }, 0);
        }
    }
});
